<template>
  <q-page padding id="inviteDetailPage">
    <div id="pageError" v-if="pageErrorMessage">
      <div class="errorMessage">{{ pageErrorMessage }}</div>
      <button class="button cancel" type="button" @click="toInvitesList">{{ $t('gen_return_to_list') }}</button>
    </div>

    <div v-if="loaded && !pageErrorMessage && invite">
      <div id="inviteDetailHeader">
        <h1>
          <q-icon name="far fa-solid fa-chevron-left" class="cursor-pointer" @click="toInvitesList" />
          {{ invite.firstName }} {{ invite.lastName }}
        </h1>
      </div>

      <div id="inviteDetailsSection">
        <div id="inviteDetails">
          <h5>{{ $t('invite_detail_title') }}</h5>

          <div class="row">
            <div class="column">
              <dl>
                <dt>{{ $t('gen_status') }}</dt>
                <dd>{{ $t(`invite_status_${invite.status.toLowerCase()}`) }}</dd>

                <dt>{{ $t('gen_email') }}</dt>
                <dd>{{ dashesIfMissing(invite.email) }}</dd>

                <dt>{{ $t('invite_label_last_email_sent') }}</dt>
                <dd>
                  {{ formatDisplayedDateTime(invite.lastEmailSentDate) }}
                  <div class="invite-resend-link-wrap">
                    <BtnLink v-if="invite.status === 'Pending'" :label="resendButtonLabel" @click="resendInviteEmail" :disable="!resendButtonEnabled" />
                  </div>
                </dd>

              </dl>
            </div>

            <div class="column">
              <dl>
                <dt>{{ $t('phone') }}</dt>
                <dd>{{ formatPhoneNumber(invite.phoneCountryCode, invite.phone) }}</dd>

                <dt>{{ $t('invite_label_phone_verification_pin') }}</dt>
                <dd>{{ dashesIfMissing(invite.phoneVerificationPin) }}</dd>

                <dt>{{ $t('invite_label_source_application') }}</dt>
                <dd>{{ dashesIfMissing(invite.sourceApplication) }}</dd>

                <dt>{{ $t('invite_label_applications') }}</dt>
                <dd>
                  <ul v-if="invite.applications && invite.applications.length > 0">
                    <li v-for="app in invite.applications" :key="app">{{ app }}</li>
                  </ul>
                  <span v-else>--</span>
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { onActivated, ref, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useQuasar } from 'quasar'
import { i18n } from '@/plugins/i18n'
import { handleApiRequest } from '@/api/handleApiRequest'
import { getInviteDetail, resendInvite, type GetInviteDetailResponse } from '@/api/endpoints/inviteWeb'
import { formatPhoneNumber } from '@/utils/phoneFormatter'
import BtnLink from '@/components/BtnLink.vue'
import { formatDisplayedDateTime } from '@/utils/dateFormatter'

const inviteId = ref(0)
const loaded = ref(false)
const pageErrorMessage = ref('')
const invite: Ref<GetInviteDetailResponse | undefined> = ref()
const $q = useQuasar()
const $route = useRoute()
const router = useRouter()
const resendButtonLabel = ref(i18n.global.t('invite_resend_invite'))
const resendButtonEnabled = ref(true)

onActivated(async () => {
  inviteId.value = $route.params?.inviteId != null ? parseInt(String($route.params.inviteId), 10) : 0
  await loadInvite()
})

const resendInviteEmail = async () => {
  $q.loading.show()

  const response = await handleApiRequest(() => resendInvite({ inviteId: inviteId.value }))
  const responseData = response?.data as any

  if (response == null || response?.data == null || responseData?.errorMessage || response.status !== 200) {
    $q.notify({
      type: 'warning',
      message: responseData?.errorMessage ?? i18n.global.t('error_unexpected_occured').toString()
    })
  } else {
    resendButtonLabel.value = i18n.global.t('invite_resend_sent')
    resendButtonEnabled.value = false
    if (invite.value && responseData?.result?.lastEmailSentDate) {
      invite.value = { ...invite.value, lastEmailSentDate: responseData.result.lastEmailSentDate }
    }
  }

  $q.loading.hide()
}

const loadInvite = async () => {
    loaded.value = false
    resendButtonLabel.value = i18n.global.t('invite_resend_invite')
    resendButtonEnabled.value = true
    $q.loading.show()

    const response = await handleApiRequest<GetInviteDetailResponse>(() => getInviteDetail(inviteId.value))

    const inviteData = response?.data as any
    
    if (inviteData?.inviteId) {
        invite.value = inviteData as GetInviteDetailResponse
    } else {
        pageErrorMessage.value = i18n.global.t('error_unexpected_occured').toString()
    }

    loaded.value = true
    $q.loading.hide()
}



const dashesIfMissing = (value: string | null | undefined) => {
  return value ? value : '--'
}

const toInvitesList = () => {
  router.push({
    name: 'Invites',
    params: {}
  })
}
</script>

<style lang="scss">
@import "@/css/styles-quasar";

#inviteDetailPage {
  #pageError {
    text-align: center;
    font-weight: bold;
    padding: $space-double;

    .errorMessage {
      margin-bottom: $space-double;
      font-size: 18px;
    }
  }

  dl {
    display: grid;
    grid-gap: 4px 16px;
    grid-template-columns: auto minmax(0, 1fr);
    align-content: flex-start;
    padding: 0;
    max-width: 100%;

    dt {
      color: $body-dim-txt-clr;
    }

    dd {
      margin: 0;
      grid-column-start: 2;
      overflow-wrap: anywhere;

      .invite-resend-link-wrap {
        display: block;
        margin-top: 4px;
      }

      ul {
        margin: 0;
        padding-left: 18px;
      }
    }
  }

  .row {
    gap: $space-base;
    justify-content: space-between;
    display: flex;
    flex-direction: row;
    max-width: 100%;

    .column {
      flex: 1;
      max-width: 48%;
    }
  }

  #inviteDetailHeader {
    margin-bottom: $space-double;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: $space-base;

    .fa-chevron-left {
      font-weight: bold;
      margin-right: $space-base;
    }
  }

  #inviteDetailsSection {
    #inviteDetails {
      @extend .panel;
      position: relative;
    }
  }

  @media screen and (max-width: $breakpoint-lg) {
    .row {
      flex-direction: column;

      .column {
        max-width: 100%;
      }
    }
  }
}
</style>
